const transformGoogleDocToPreview = (
  url: string,
  docId: string,
  reason: string
): string => {
  const previewUrl = `https://docs.google.com/document/d/${docId}/preview`;
  console.warn('LookerReport: transformed Google Doc URL', {
    original: url,
    transformed: previewUrl,
    reason,
  });
  return previewUrl;
};

const transformGoogleDriveToPreview = (url: string, fileId: string): string => {
  const previewUrl = `https://drive.google.com/file/d/${fileId}/preview`;
  console.warn('LookerReport: transformed Google Drive URL', {
    original: url,
    transformed: previewUrl,
    reason: 'drive->preview',
  });
  return previewUrl;
};

const isPublishedGoogleDoc = (url: string): boolean => {
  return (
    url.includes('docs.google.com/document') &&
    /\/d\/e\/([a-zA-Z0-9_-]+)/.test(url)
  );
};

const isGoogleSpreadsheet = (url: string): boolean => {
  return url.includes('spreadsheets');
};

const isGoogleDocument = (url: string): boolean => {
  return url.includes('docs.google.com/document');
};

const transformPublishedSheetPdfToHtml = (url: string): string => {
  if (url.includes('/pub?output=pdf') || url.includes('/pub&output=pdf')) {
    const htmlUrl = url.replace(/\/pub[?&]output=pdf/, '/pubhtml');
    console.warn('LookerReport: transformed published sheet PDF to HTML', {
      original: url,
      transformed: htmlUrl,
      reason: 'pub-pdf-blocked->pubhtml',
    });
    return htmlUrl;
  }
  return url;
};

const transformPdfToGview = (url: string): string => {
  const encoded = encodeURIComponent(url);
  const gview = `https://docs.google.com/gview?url=${encoded}&embedded=true`;
  console.warn('LookerReport: transformed PDF URL to Google Viewer', {
    original: url,
    transformed: gview,
    reason: 'pdf->gview',
  });
  return gview;
};

export const getEmbedUrl = (url: string): string => {
  if (!url) return url;

  const trimmed = url.trim();

  if (isPublishedGoogleDoc(trimmed)) {
    return trimmed;
  }

  const docMatch = trimmed.match(/\/document\/d\/([a-zA-Z0-9_-]+)/);
  if (isGoogleDocument(trimmed) && docMatch) {
    return transformGoogleDocToPreview(
      trimmed,
      docMatch[1],
      'document->preview'
    );
  }

  const genericDocMatch = trimmed.match(
    /\/d\/([a-zA-Z0-9_-]+)\/(?:edit|pub|preview|view)?/
  );
  if (
    isGoogleDocument(trimmed) &&
    !isGoogleSpreadsheet(trimmed) &&
    genericDocMatch
  ) {
    return transformGoogleDocToPreview(
      trimmed,
      genericDocMatch[1],
      'generic-d->preview'
    );
  }

  if (isGoogleSpreadsheet(trimmed)) {
    return transformPublishedSheetPdfToHtml(trimmed);
  }

  const driveMatch = trimmed.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (trimmed.includes('drive.google.com') && driveMatch) {
    return transformGoogleDriveToPreview(trimmed, driveMatch[1]);
  }

  // If it's a PDF (remote or published), use Google Docs viewer to improve
  // iframe compatibility (gview will embed external PDFs in an iframe).
  if (isPdfUrl(trimmed)) {
    return transformPdfToGview(trimmed);
  }

  return url;
};

export const decodeUrlEntities = (url: string): string => {
  return url.replace(/&amp;/g, '&');
};

export const isTrustedUrl = (
  url: string,
  trustedDomains: readonly string[]
): boolean => {
  return trustedDomains.some((domain) => url.includes(domain));
};

export const generateSheetPdfExportUrl = (
  url: string,
  sheetId: string,
  gid: string = '0'
): string => {
  return `https://docs.google.com/spreadsheets/d/${sheetId}/export?format=pdf&gid=${gid}&single=true`;
};

export const extractSheetId = (url: string): string | null => {
  const match = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
  return match ? match[1] : null;
};

/**
 * Extracts the gid (sheet tab ID) from a Google Sheets URL
 */
export const extractGid = (url: string): string => {
  const gidMatch = url.match(/[?&]gid=(\d+)|#gid=(\d+)/);
  return gidMatch ? gidMatch[1] || gidMatch[2] : '0';
};

// Exported helper to detect PDF URLs so callers can add viewer-specific UI tweaks
export const isPdfUrl = (url: string): boolean => {
  return (
    /\.pdf(\?|$)/i.test(url) ||
    /[?&]output=pdf/i.test(url) ||
    url.includes('docs.google.com/gview')
  );
};
